"use client";

import { cn } from "@frontend/shared/lib/utils";
import Fuse from "fuse.js";
import { ChevronDown, X } from "lucide-react";
import * as React from "react";
import { createPortal } from "react-dom";
import { KioskInput } from "./KioskInput";

export interface SearchOption {
  value: string;
  label: string;
}

interface SearchOptionWithMatches extends SearchOption {
  matches?: Array<[number, number]>; // Array of [start, end] indices for highlighting
}

// ============================================================================
// Context
// ============================================================================

interface KioskSearchDropdownContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  value?: string;
  onChange?: (value: string) => void;
  options: SearchOption[];
  searchText: string;
  setSearchText: (text: string) => void;
  highlightedIndex: number;
  setHighlightedIndex: React.Dispatch<React.SetStateAction<number>>;
  filteredOptions: SearchOptionWithMatches[];
  error?: boolean;
  disabled?: boolean;
  triggerRef: React.RefObject<HTMLDivElement | null>;
  contentRef: React.RefObject<HTMLDivElement | null>;
}

const KioskSearchDropdownContext =
  React.createContext<KioskSearchDropdownContextValue | null>(null);

const useKioskSearchDropdown = () => {
  const context = React.useContext(KioskSearchDropdownContext);
  if (!context) {
    throw new Error(
      "KioskSearchDropdown components must be used within KioskSearchDropdown",
    );
  }
  return context;
};

// ============================================================================
// Root Component
// ============================================================================

interface KioskSearchDropdownProps {
  children: React.ReactNode;
  options: SearchOption[];
  value?: string;
  onChange?: (value: string) => void;
  error?: boolean;
  disabled?: boolean;
  /**
   * Custom filter function. By default, filters by label containing search text (case-insensitive)
   */
  filterFn?: (option: SearchOption, searchText: string) => boolean;
  /**
   * Max number of results to show in dropdown
   */
  maxResults?: number;
}

const KioskSearchDropdown = React.forwardRef<
  HTMLDivElement,
  KioskSearchDropdownProps
>(
  (
    {
      children,
      options,
      value,
      onChange,
      error,
      disabled,
      filterFn,
      maxResults = 60,
    },
    ref,
  ) => {
    const [open, setOpen] = React.useState(false);
    const [searchText, setSearchText] = React.useState("");
    const [highlightedIndex, setHighlightedIndex] = React.useState(0);
    const triggerRef = React.useRef<HTMLDivElement>(null);
    const contentRef = React.useRef<HTMLDivElement>(null);

    // Initialize Fuse.js for fuzzy searching
    const fuse = React.useMemo(
      () =>
        new Fuse(options, {
          keys: ["label", "value"],
          threshold: 0.3, // 0 = exact match, 1 = match anything
          distance: 100, // Maximum Levenshtein distance
          includeScore: true,
          includeMatches: true, // Include match indices for highlighting
        }),
      [options],
    );

    // Filter options based on search text using Fuse.js
    const filteredOptions = React.useMemo(() => {
      const text = searchText.trim();

      // If no search text, show all options (up to maxResults)
      if (!text) return options.slice(0, maxResults);

      // If custom filter function provided, use it
      if (filterFn) {
        return options
          .filter((opt) => filterFn(opt, text))
          .slice(0, maxResults);
      }

      // Otherwise use Fuse.js fuzzy search with match highlighting
      const results = fuse.search(text, { limit: maxResults });

      return results.map((result) => {
        // Extract match indices from Fuse.js result
        const matches = result.matches
          ?.filter((match) => match.key === "label")
          .flatMap((match) => match.indices || []);

        return {
          ...result.item,
          matches,
        };
      });
    }, [searchText, options, filterFn, maxResults, fuse]);

    // Reset highlighted index when filtered options change
    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    React.useEffect(() => {
      setHighlightedIndex(0);
    }, [filteredOptions]);

    return (
      <KioskSearchDropdownContext.Provider
        value={{
          open,
          setOpen,
          value,
          onChange,
          options,
          searchText,
          setSearchText,
          highlightedIndex,
          setHighlightedIndex,
          filteredOptions,
          error,
          disabled,
          triggerRef,
          contentRef,
        }}
      >
        <div ref={ref} className="relative w-full">
          {children}
        </div>
      </KioskSearchDropdownContext.Provider>
    );
  },
);

KioskSearchDropdown.displayName = "KioskSearchDropdown";

// ============================================================================
// Trigger Component (Input)
// ============================================================================

interface KioskSearchDropdownTriggerProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  placeholder?: string;
}

const KioskSearchDropdownTrigger = React.forwardRef<
  HTMLInputElement,
  KioskSearchDropdownTriggerProps
>(({ placeholder = "Search...", className, ...props }, ref) => {
  const {
    open,
    setOpen,
    value,
    onChange,
    options,
    searchText,
    setSearchText,
    error,
    disabled,
    triggerRef,
    contentRef,
    filteredOptions,
    highlightedIndex,
    setHighlightedIndex,
  } = useKioskSearchDropdown();

  // Get selected option label for display
  const selectedOption = options.find((opt) => opt.value === value);
  const displayText = selectedOption?.label || "";

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    setOpen(true);
  };

  // Handle focus
  const handleFocus = () => {
    setOpen(true);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) {
      if (e.key === "Enter" || e.key === "ArrowDown") {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : prev,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case "Enter":
        e.preventDefault();
        if (filteredOptions[highlightedIndex]) {
          onChange?.(filteredOptions[highlightedIndex].value);
          setOpen(false);
          setSearchText("");
        }
        break;
      case "Escape":
        e.preventDefault();
        setOpen(false);
        setSearchText("");
        break;
    }
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: PointerEvent) => {
      const target = event.target as Node;
      const clickedTrigger = triggerRef.current?.contains(target);
      const clickedContent = contentRef.current?.contains(target);

      if (!clickedTrigger && !clickedContent) {
        if (!open) {
          setSearchText("");
          return;
        }
        // Auto-select if there's exactly one matching option
        if (filteredOptions.length === 1) {
          onChange?.(filteredOptions[0].value);
        }
        setOpen(false);
        setSearchText("");
      }
    };

    document.addEventListener("pointerdown", handleClickOutside);
    return () => {
      document.removeEventListener("pointerdown", handleClickOutside);
    };
  }, [
    open,
    setOpen,
    setSearchText,
    triggerRef,
    contentRef,
    filteredOptions,
    onChange,
  ]);

  return (
    <div ref={triggerRef} className="relative">
      <KioskInput
        ref={ref}
        type="text"
        value={open ? searchText : displayText}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        error={error}
        className={cn("pr-12", className)}
        {...props}
      />

      {/* Chevron/Clear Icon */}
      {value && !disabled ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onChange?.("");
            setSearchText("");
            setOpen(false);
          }}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      ) : (
        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
      )}
    </div>
  );
});

KioskSearchDropdownTrigger.displayName = "KioskSearchDropdownTrigger";

// ============================================================================
// Content Component (Dropdown)
// ============================================================================

interface KioskSearchDropdownContentProps
  extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

const KioskSearchDropdownContent = React.forwardRef<
  HTMLDivElement,
  KioskSearchDropdownContentProps
>(({ children, className, ...props }, ref) => {
  const { open, disabled, triggerRef, contentRef } = useKioskSearchDropdown();
  const [position, setPosition] = React.useState({
    top: 0,
    left: 0,
    width: 0,
    maxHeight: 308,
  });

  // Calculate position based on trigger element
  React.useEffect(() => {
    const updatePosition = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const gap = 8;
      const padding = 16;
      const viewportHeight =
        window.visualViewport?.height ?? window.innerHeight;
      const availableBelow = viewportHeight - rect.bottom - gap - padding;
      setPosition({
        top: rect.bottom + window.scrollY + gap,
        left: rect.left + window.scrollX,
        width: rect.width,
        maxHeight: Math.min(308, Math.max(availableBelow, 120)),
      });
    };

    const centerAndUpdate = () => {
      triggerRef.current?.scrollIntoView?.({
        block: "center",
        behavior: "instant",
      });
      updatePosition();
    };

    if (open) {
      centerAndUpdate();
      window.visualViewport?.addEventListener("resize", centerAndUpdate);
      window.visualViewport?.addEventListener("scroll", updatePosition);
    }

    return () => {
      window.visualViewport?.removeEventListener("resize", centerAndUpdate);
      window.visualViewport?.removeEventListener("scroll", updatePosition);
    };
  }, [open, triggerRef]);

  if (!open || disabled) return null;

  // Use portal to render outside the container to prevent clipping
  return createPortal(
    <div
      ref={contentRef}
      className={cn(
        "fixed z-50",
        "bg-white rounded-2xl border border-gray-100 shadow-xl",
        "overflow-y-auto",
        className,
      )}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: `${position.width}px`,
        maxHeight: `${position.maxHeight}px`,
      }}
      {...props}
    >
      {children}
    </div>,
    document.body,
  );
});

KioskSearchDropdownContent.displayName = "KioskSearchDropdownContent";

// ============================================================================
// List Component
// ============================================================================

interface KioskSearchDropdownListProps
  extends React.HTMLAttributes<HTMLUListElement> {
  children: React.ReactNode;
}

const KioskSearchDropdownList = React.forwardRef<
  HTMLUListElement,
  KioskSearchDropdownListProps
>(({ children, className, ...props }, ref) => {
  return (
    <ul ref={ref} className={cn("py-2 px-2", className)} {...props}>
      {children}
    </ul>
  );
});

KioskSearchDropdownList.displayName = "KioskSearchDropdownList";

// ============================================================================
// Item Component
// ============================================================================

// Helper function to highlight matching characters
function highlightMatches(text: string, matches?: Array<[number, number]>) {
  if (!matches || matches.length === 0) {
    return <span>{text}</span>;
  }

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  // Sort matches by start index
  const sortedMatches = [...matches].sort((a, b) => a[0] - b[0]);

  sortedMatches.forEach(([start, end], i) => {
    // Add non-matching text before this match
    if (start > lastIndex) {
      parts.push(
        <span
          key={`text-${
            // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
            i
          }`}
        >
          {text.substring(lastIndex, start)}
        </span>,
      );
    }

    // Add matching text with highlight
    parts.push(
      <mark
        key={`match-${
          // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
          i
        }`}
        className="text-kioskSecondary bg-transparent font-medium"
      >
        {text.substring(start, end + 1)}
      </mark>,
    );

    lastIndex = end + 1;
  });

  // Add remaining text after last match
  if (lastIndex < text.length) {
    parts.push(<span key="text-end">{text.substring(lastIndex)}</span>);
  }

  return <>{parts}</>;
}

interface KioskSearchDropdownItemProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  option: SearchOptionWithMatches;
  index: number;
}

const KioskSearchDropdownItem = React.forwardRef<
  HTMLButtonElement,
  KioskSearchDropdownItemProps
>(({ option, index, className, ...props }, ref) => {
  const {
    value,
    onChange,
    setOpen,
    setSearchText,
    highlightedIndex,
    setHighlightedIndex,
  } = useKioskSearchDropdown();

  const isHighlighted = highlightedIndex === index;

  const handleSelect = () => {
    onChange?.(option.value);
    setSearchText("");
    setOpen(false);
  };

  return (
    <li>
      <button
        ref={ref}
        type="button"
        onClick={handleSelect}
        onMouseEnter={() => setHighlightedIndex(index)}
        className={cn(
          "w-full text-left px-5 py-3 text-base transition-colors rounded-xl",
          "hover:bg-gray-100",
          isHighlighted && "bg-gray-100",
          className,
        )}
        {...props}
      >
        {highlightMatches(option.label, option.matches)}
      </button>
    </li>
  );
});

KioskSearchDropdownItem.displayName = "KioskSearchDropdownItem";

// ============================================================================
// Empty Component
// ============================================================================

interface KioskSearchDropdownEmptyProps
  extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

const KioskSearchDropdownEmpty = React.forwardRef<
  HTMLDivElement,
  KioskSearchDropdownEmptyProps
>(({ children = "No results found", className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn("px-6 py-8 text-center text-gray-500 text-base", className)}
      {...props}
    >
      {children}
    </div>
  );
});

KioskSearchDropdownEmpty.displayName = "KioskSearchDropdownEmpty";

// ============================================================================
// Helper Component - Auto-renders filtered items
// ============================================================================

interface KioskSearchDropdownItemsProps {
  emptyMessage?: string;
}

const KioskSearchDropdownItems = ({
  emptyMessage = "No results found",
}: KioskSearchDropdownItemsProps) => {
  const { filteredOptions } = useKioskSearchDropdown();

  if (filteredOptions.length === 0) {
    return <KioskSearchDropdownEmpty>{emptyMessage}</KioskSearchDropdownEmpty>;
  }

  return (
    <KioskSearchDropdownList>
      {filteredOptions.map((option, index) => (
        <KioskSearchDropdownItem
          key={option.value}
          option={option}
          index={index}
        />
      ))}
    </KioskSearchDropdownList>
  );
};

KioskSearchDropdownItems.displayName = "KioskSearchDropdownItems";

// ============================================================================
// Exports
// ============================================================================

export {
  useKioskSearchDropdown,
  KioskSearchDropdown,
  KioskSearchDropdownTrigger,
  KioskSearchDropdownContent,
  KioskSearchDropdownList,
  KioskSearchDropdownItem,
  KioskSearchDropdownEmpty,
  KioskSearchDropdownItems,
};
